import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Col, Container, media, Row, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {InViewAnimContainer} from 'UI/components/layout/InViewAnimContainer/InViewAnimContainer';
import {StorePartner} from 'UI/components/layout/StorePartner';
import {H2, H5, P} from 'UI/components/layout/Text';

import {FadeInAnimation} from 'UI/animations/shared';

import {useOnlineShops} from './useOnlineShops';
import {transient} from 'Application/configs/emotionTransientProps';
import {isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';

import type {OnlineShopsBlock} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected by the `OnlineShops` component.
 * It outlines all required and optional properties needed to render the online shops section with theme, content, and partner logos.
 * This interface leverages the `OnlineShopsBlock` type from the Payload CMS schema to ensure type consistency between the CMS data and the component.
 */
interface ComponentProps {
    /**
     * The headline text displayed as the main title of the hero section.
     */
    headline: OnlineShopsBlock['headline'];
    /**
     * The overline displayed above the headline providing additional context or description.
     */
    overline: OnlineShopsBlock['overline'];
    /**
     * An array of partner objects, each containing an ID, logo media, and website URL for store links.
     */
    partners: OnlineShopsBlock['partners'];
    /**
     * The subline displayed below the headline providing additional context or description.
     */
    subline: OnlineShopsBlock['subline'];
    /**
     * A unique identifier assigned to the component for testing purposes, enabling reliable element targeting in automated tests.
     *
     * @default 'OnlineShops'
     */
    testId?: string;
    /**
     * The optional theme data attribute applied to the component for switching between light and dark color schemes.
     */
    theme?: OnlineShopsBlock['theme'];
}

/**
 * The `OnlineShops` component renders the online shops section of the promotional landing page, featuring text content and partner store links.
 *
 * @param props          The component props.
 * @param props.headline The main headline text displayed prominently in the section.
 * @param props.overline The overline text displayed above the headline, providing additional context or description.
 * @param props.partners An array of partner store objects containing logos and website URLs for direct shop links.
 * @param props.subline  The subline text rendered below the headline.
 * @param props.testId   An optional unique identifier for testing purposes, defaulting to `'OnlineShops'`.
 * @param props.theme    The optional theme attribute for switching between light and dark color schemes.
 * @returns A JSX element representing the hero section with background, product image, content, and partner links.
 *
 * @example
 * ```tsx
 * <OnlineShops
 *   headline="Sonos Era 300"
 *   overline="The future of sound is here"
 *   partners={[
 *     {id: '1', logo: mediaObject, website: 'https://store.example.com'}
 *   ]}
 *   subline="Experience spatial audio like never before."
 *   theme="dark"
 * />
 * ```
 */
const OnlineShops = observer(({
    headline,
    overline,
    partners,
    subline,
    testId = 'OnlineShops',
    theme
}: ComponentProps) => {
    const {colors} = useOnlineShops();

    return (
        <Wrapper data-nfq-theme={theme}>
            <StyledContainer data-cy={testId}>
                <Row>
                    <InViewAnimContainer baseComponent={Row}>
                        <Col
                            lg={6}
                            offset={{
                                xs: 0,
                                xxl: 1
                            }}
                            xl={5}
                            xs={12}
                            xxl={4}
                        >
                            <TextWrapper>
                                <MotionH5 variants={FadeInAnimation}>
                                    <Color $color={colors.secondary}>
                                        {overline}
                                    </Color>
                                </MotionH5>
                                <Spacer y={6} isNotStretching />
                                <MotionH2 variants={FadeInAnimation}>
                                    {headline}
                                </MotionH2>
                                <Spacer y={6} isNotStretching />
                                <MotionP variants={FadeInAnimation}>
                                    <Color $color={colors.secondary}>
                                        {subline}
                                    </Color>
                                </MotionP>
                            </TextWrapper>
                        </Col>
                        <Col
                            lg={5}
                            offset={{
                                md: 0,
                                lg: 1,
                                xxl: 2
                            }}
                            xs={12}
                            xxl={4}
                        >
                            <Spacer
                                y={{
                                    xs: 3,
                                    lg: 0
                                }}
                                isNotStretching
                            />
                            <P $size="small">
                                <Color $color={colors.primary}>
                                    Erhältlich in den Online Shops unserer Partner:
                                </Color>
                            </P>
                            <Spacer y={4} isNotStretching />
                            <StoreCardWrapper>
                                {isDefinedAndPopulated(partners) && partners.map(partner => {
                                    if (!isDefinedAndPopulated(partner)) return null;

                                    return (
                                        <StorePartner
                                            key={partner.id}
                                            logo={partner.logo}
                                            url={partner.website}
                                        />
                                    );
                                })}
                            </StoreCardWrapper>
                        </Col>
                    </InViewAnimContainer>
                </Row>
            </StyledContainer>
        </Wrapper>
    );
});

OnlineShops.displayName = 'OnlineShops';

export {OnlineShops};

const Wrapper = styled.div`
    background-color: ${({theme}) => theme.colors.bgPrimary};
`;

const StyledContainer = styled(Container)`
    padding-block: ${spacing(14)};

    ${media('md')} {
        padding-block: ${spacing(20)};
    }
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin: auto 0;
`;

const H5P = H5.withComponent('p');

const MotionH5 = styled(motion.create(H5P), {...transient})``;

const MotionH2 = styled(motion.create(H2), {...transient})`
    color: ${({theme}) => theme.colors.primary};
`;

const MotionP = styled(motion.create(P), {...transient})``;

const StoreCardWrapper = styled.div`
    display: grid;
    gap: ${spacing(4)};
    grid-template-columns: repeat(1, 1fr);

    ${media('sm')} {
        grid-template-columns: repeat(2, 1fr);
    }
    ${media('md')} {
        grid-template-columns: repeat(3, 1fr);
    }
    ${media('lg')} {
        grid-template-columns: repeat(2, 1fr);
    }
`;